import { Link, useNavigate, useParams } from "react-router-dom"
import { Button } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { addQuestion, editQuestion, listQuestions } from "../../../services/QuestionsService"
import { useState, useEffect } from "react"
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService"
import Loader from "../Common/Loader"
import Select from "react-select"
import MedicineDropdown from "../AppsMenu/Patients/Components/MedicineDropdown"
import { getNoteCategoriesAction } from "../../../store/actions/NoteCategoriesAction";

// Helper function to convert API question type label to internal value
const getTypeFromLabel = (label) => {
  switch (label) {
    case "Short Answer":
      return "short_answer"
    case "Paragraph":
      return "paragraph"
    case "Single Selection":
      return "checkbox"
    case "Multiple Selection":
      return "multiple_choice"
    case "Date":
      return "date"
    case "Time":
      return "time"
    case "File Input":
      return "File Input"
    default:
      return label.toLowerCase().replace(/ /g, "_")
  }
}

// Helper function to capitalize the first letter of a string
const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

const AddQuestion = () => {
  const dispatch = useDispatch()
  const [selectedMedicines, setSelectedMedicines] = useState([])
  const [selectionType, setSelectionType] = useState("multi")
  const [question, setQuestion] = useState("")
  const [questionData, setQuestionData] = useState(null)
  const [questionType, setQuestionType] = useState("short_answer")
  const [isToggleOn, setIsToggleOn] = useState(false)
  const [isFirstSwitchOn, setIsFirstSwitchOn] = useState(false)
  const [noteCategory, setNoteCategory] = useState(null);
  const [multipleChoiceOptions, setMultipleChoiceOptions] = useState([
    { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
    { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
  ])
  const [checkboxOptions, setCheckboxOptions] = useState([
    { option_name: "Option 1", option_type: "neutral", medicines: [] },
    { option_name: "Option 2", option_type: "neutral", medicines: [] },
  ])
  const [fields, setFields] = useState([{
    validationType: "", criteria: "", field1: "", field2: "", fileType: "",
    maxSize: "",
    label: ""
  }])
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState("")
  const [allowOtherOption, setAllowOtherOption] = useState(false)
  const [shortForm, setShortForm] = useState("");
  const [selectedNotesCategory, setSelectedNotesCategory] = useState(null);
  const noteCategories = useSelector(
    (state) => state.noteCategory.list || []
  );

  // Create notesCategoryOptions from noteCategories
  const notesCategoryOptions = noteCategories.map((cat) => ({
    value: cat.id,
    label: cat.name
  }));

  // Options for dropdowns
  const questionTypeOptions = [
    { value: "short_answer", label: "Short Answer" },
    { value: "paragraph", label: "Paragraph" },
    { value: "checkbox", label: "Single Selection" },
    { value: "multiple_choice", label: "Multiple Selection" },
    { value: "date", label: "Date" },
    { value: "time", label: "Time" },
    { value: "File Input", label: "File input" },
  ]

  const validationTypeOptions = [
    { value: "", label: "Select Validation Type" },
    { value: "number", label: "Number" },
    { value: "text", label: "Text" },
    { value: "length", label: "Length" },
    { value: "regex", label: "Regular Expression" },
  ]

  const numberCriteriaOptions = [
    { value: "", label: "Select Criteria" },
    { value: "greater_than", label: "Greater Than" },
    { value: "greater_than_equal", label: "Greater Than or Equal To" },
    { value: "less_than", label: "Less Than" },
    { value: "less_than_equal", label: "Less Than or Equal To" },
    { value: "between", label: "Between" },
    { value: "not_between", label: "Not Between" },
    { value: "is_number", label: "Is Number" },
    { value: "whole_number", label: "Whole Number" },
  ]

  const textCriteriaOptions = [
    { value: "", label: "Select Criteria" },
    { value: "contains", label: "Contains" },
    { value: "does_not_contain", label: "Does Not Contain" },
    { value: "email", label: "Email" },
    { value: "url", label: "URL" },
  ]

  const lengthCriteriaOptions = [
    { value: "", label: "Select Criteria" },
    { value: "min", label: "Minimum Length" },
    { value: "max", label: "Maximum Length" },
  ]

  const regexCriteriaOptions = [
    { value: "", label: "Select Criteria" },
    { value: "contains", label: "Contains" },
    { value: "does_not_contain", label: "Does Not Contain" },
    { value: "matches", label: "Matches" },
    { value: "does_not_match", label: "Does Not Match" },
  ]

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "50px",
      height: "50px",
    }),
    singleValue: (provided) => ({
      ...provided,
      lineHeight: "50px",
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "4px",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      position: "absolute",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  }

  const getCriteriaOptions = (validationType) => {
    switch (validationType) {
      case "number":
        return numberCriteriaOptions
      case "text":
        return textCriteriaOptions
      case "length":
        return lengthCriteriaOptions
      case "regex":
        return regexCriteriaOptions
      default:
        return [{ value: "", label: "Select Criteria" }]
    }
  }

  // Helper function to convert API validation type to dropdown value
  const getValidationTypeValue = (apiValidationType) => {
    if (!apiValidationType) return ""
    const lowerType = apiValidationType.toLowerCase()
    switch (lowerType) {
      case "number":
        return "number"
      case "text":
        return "text"
      case "length":
        return "length"
      case "regular expression":
      case "regex":
        return "regex"
      default:
        return lowerType
    }
  }

  // Helper function to convert API criteria to dropdown value
  const getCriteriaValue = (apiCriteria) => {
    if (!apiCriteria) return ""
    const lowerCriteria = apiCriteria.toLowerCase().replace(/ /g, "_")
    switch (lowerCriteria) {
      case "greater_than":
        return "greater_than"
      case "greater_than_or_equal_to":
        return "greater_than_equal"
      case "less_than":
        return "less_than"
      case "less_than_or_equal_to":
        return "less_than_equal"
      case "between":
        return "between"
      case "not_between":
        return "not_between"
      case "is_number":
        return "is_number"
      case "whole_number":
        return "whole_number"
      case "contains":
        return "contains"
      case "does_not_contain":
        return "does_not_contain"
      case "email":
        return "email"
      case "url":
        return "url"
      case "minimum_length":
      case "min":
        return "min"
      case "maximum_length":
      case "max":
        return "max"
      case "matches":
        return "matches"
      case "does_not_match":
        return "does_not_match"
      default:
        return lowerCriteria
    }
  }

  useEffect(() => {
    console.log("[v0] Component mounted, loading note categories");
    dispatch(getNoteCategoriesAction(false)); // pagination=false
  }, []); // Empty array - load only once on mount

  // Note: Do NOT load all medicines here. The MedicineDropdown component 
  // handles its own lazy loading with pagination. This prevents infinite API calls.
  // The getMedicinesAction is removed to avoid duplicate calls.

  // Fetch question details if editing
  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        setFetching(true)
        setError("")
        try {
          const loginDetails = JSON.parse(localStorage.getItem("loginDetails"))
          const admin_id = loginDetails?.id || 1
          const res = await listQuestions({ admin_id })
          if (res.data.status === "success") {
            const q = res.data.data.find((q) => q.question_id === id || q.question_id === String(id))
            if (q) {
              setQuestion(q.question)
              setShortForm(q.short_form || "")
              const fetchedQuestionType = getTypeFromLabel(q.question_type)
              setQuestionType(fetchedQuestionType)
              if (fetchedQuestionType === "File Input") {
                setFields([{
                  validationType: "",
                  criteria: "",
                  field1: "",
                  field2: "",
                  fileType: q.allowed_file_type
                    ? q.allowed_file_type.split(",").map(t => t.toLowerCase())
                    : [],
                  maxSize: q.max_file_size_mb || "",
                  label: q.file_lable || ""
                }]);
              }

              setIsToggleOn(q.is_required === "Yes")
              setIsFirstSwitchOn(q.is_important === "Yes")

              if (q.options) {
                let opts = []
                if (typeof q.options === "string") {
                  try {
                    opts = JSON.parse(q.options)
                  } catch {
                    opts = []
                  }
                } else if (Array.isArray(q.options)) {
                  opts = q.options
                }

                const hasOtherOption = opts.some((o) => o.option_name === "Other" && o.option_type === "Other")
                setAllowOtherOption(hasOtherOption)

                const filteredOpts = opts.filter((o) => !(o.option_name === "Other" && o.option_type === "Other"))
                const newOptions = filteredOpts.map((o) => ({
                  option_name: o.option_name || o.value || "",
                  option_type: (o.option_type?.toLowerCase() || o.sentiment || "neutral").toLowerCase(),
                  note_category_id: o.note_category_id || null,
                  medicines: Array.isArray(o.medicines) ? o.medicines.map((m) => String(m.medicine_id)) : [],
                }))

                if (fetchedQuestionType === "checkbox" || fetchedQuestionType === "multiple_choice") {
                  setMultipleChoiceOptions(newOptions.length > 0 ? newOptions : [
                    { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
                    { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
                  ])
                  setCheckboxOptions([])
                } else {
                  setMultipleChoiceOptions([
                    { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
                    { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
                  ])
                  setCheckboxOptions([
                    { option_name: "Option 1", option_type: "neutral", medicines: [] },
                    { option_name: "Option 2", option_type: "neutral", medicines: [] },
                  ])
                }
              } else {
                setMultipleChoiceOptions([
                  { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
                  { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
                ])
                setCheckboxOptions([
                  { option_name: "Option 1", option_type: "neutral", medicines: [] },
                  { option_name: "Option 2", option_type: "neutral", medicines: [] },
                ])
                setAllowOtherOption(false)
              }

              if (fetchedQuestionType !== "File Input") {
                if (q.validation_json_response) {
                  const v = JSON.parse(q.validation_json_response)
                  const newFields = v.map((val) => ({
                    validationType: getValidationTypeValue(val.response_validation),
                    criteria: getCriteriaValue(val.criteria),
                    field1: val.response || "",
                    field2: "",
                  }))
                  setFields(newFields)
                } else {
                  setFields([{ validationType: "", criteria: "", field1: "", field2: "" }])
                }
              }
            }
          }
        } catch {
          setError("Error loading question for edit")
        } finally {
          setFetching(false)
        }
      }
      fetchData()
    } else {
      setQuestion("")
      setQuestionType("short_answer")
      setIsToggleOn(false)
      setIsFirstSwitchOn(false)
      setMultipleChoiceOptions([
        { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
        { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
      ])
      setCheckboxOptions([
        { option_name: "Option 1", option_type: "neutral", medicines: [] },
        { option_name: "Option 2", option_type: "neutral", medicines: [] },
      ])
      setFields([{ validationType: "", criteria: "", field1: "", field2: "" }])
      setAllowOtherOption(false)
    }
  }, [id, dispatch])

  const handleQuestionChange = (e) => {
    setQuestion(e.target.value)
    validateForm()
  }

  const handleTypeChange = (selectedOption) => {
    setQuestionType(selectedOption.value)
    validateForm()
    if (selectedOption.value !== "checkbox" && selectedOption.value !== "multiple_choice") {
      setAllowOtherOption(false)
    }
    if (selectedOption.value === "checkbox" && multipleChoiceOptions.length === 0) {
      setMultipleChoiceOptions([
        { option_name: "Option 1", option_type: "neutral", medicines: [], note_category_id: null },
        { option_name: "Option 2", option_type: "neutral", medicines: [], note_category_id: null },
      ])
    }
  }

  const validateForm = () => {
    // No-op or add your own validation logic if needed
  }

  // Helper to get formatted question type
  const getFormattedType = (type) => {
    switch (type) {
      case "short_answer":
        return "Short Answer"
      case "paragraph":
        return "Paragraph"
      case "checkbox":
        return "Single Selection"
      case "multiple_choice":
        return "Multiple Selection"
      case "Multiple Choice":
        return "Multiple Selection"
      case "Checkbox":
        return "Single Selection"
      case "date":
        return "Date"
      case "time":
        return "Time"
      default:
        return type
    }
  }

  // Helper to format criteria for API
  const formatCriteriaForAPI = (criteria) => {
    switch (criteria) {
      case "greater_than":
        return "Greater Than"
      case "greater_than_equal":
        return "Greater Than or Equal To"
      case "less_than":
        return "Less Than"
      case "less_than_equal":
        return "Less Than or Equal To"
      case "between":
        return "Between"
      case "not_between":
        return "Not Between"
      case "is_number":
        return "Is Number"
      case "whole_number":
        return "Whole Number"
      case "contains":
        return "Contains"
      case "does_not_contain":
        return "Does Not Contain"
      case "email":
        return "Email"
      case "url":
        return "URL"
      case "min":
        return "Minimum Length"
      case "max":
        return "Maximum Length"
      case "matches":
        return "Matches"
      case "does_not_match":
        return "Does Not Match"
      default:
        return criteria.charAt(0).toUpperCase() + criteria.slice(1)
    }
  }

  // Helper to format validation type for API
  const formatValidationTypeForAPI = (validationType) => {
    switch (validationType) {
      case "number":
        return "Number"
      case "text":
        return "Text"
      case "length":
        return "Length"
      case "regex":
        return "Regular Expression"
      default:
        return validationType.charAt(0).toUpperCase() + validationType.slice(1)
    }
  }

  // Helper to build options array
  const buildOptions = (type, multipleChoiceOptions, checkboxOptions) => {
    const formatOptions = (options) =>
      options.map((opt) => ({
        option_name: opt.option_name,
        option_type: capitalizeFirstLetter(opt.option_type || "neutral"),
        medicines: opt.medicines || [],
        note_category_id: opt.note_category_id ?? noteCategory?.value ?? null
      }))

    let optionsToBuild = []
    if (type === "multiple_choice" || type === "checkbox") {
      const safeOptions = Array.isArray(multipleChoiceOptions) ? multipleChoiceOptions : []
      optionsToBuild = formatOptions(safeOptions)
    }

    if (allowOtherOption && (type === "multiple_choice" || type === "checkbox")) {
      if (!optionsToBuild.some((opt) => opt.option_name === "Other" && opt.option_type === "Other")) {
        optionsToBuild.push({ option_name: "Other", option_type: "Other", medicines: [] })
      }
    }

    return optionsToBuild.length > 0 ? optionsToBuild : undefined
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const loginDetails = JSON.parse(localStorage.getItem("loginDetails"));
      const admin_id = loginDetails?.id || 1;
      const token = loginDetails?.token || "";

      let allowed_file_type = "";
      let max_file_size_mb = "";
      let file_lable = "";

      if (questionType === "File Input" && fields.length > 0) {
        const fileField = fields[0];
        allowed_file_type = Array.isArray(fileField.fileType)
          ? fileField.fileType.map(t => t.toUpperCase()).join(",")
          : fileField.fileType?.toUpperCase() || "";
        max_file_size_mb = fileField.maxSize || "";
        file_lable = fileField.label || "";
      }

      const validations = fields
        .filter((f) => f.validationType && f.criteria)
        .map((f) => ({
          response_validation: formatValidationTypeForAPI(f.validationType),
          criteria: formatCriteriaForAPI(f.criteria),
          response: f.field1 || "",
        }));

      const optionsArr = buildOptions(questionType, multipleChoiceOptions, checkboxOptions);

      const payload = {
        token,
        admin_id,
        question,
        question_type: getFormattedType(questionType),
        short_form: shortForm || "",
        is_required: isToggleOn ? "Yes" : "No",
        is_important: isFirstSwitchOn ? "Yes" : "No",
        is_other: allowOtherOption ? "Yes" : "No",
        validations: JSON.stringify(validations),
        options: optionsArr ? JSON.stringify(optionsArr) : "[]",
        allowed_file_type,
        max_file_size_mb,
        file_lable
      };
      console.log("Payload:", payload)

      if (id) payload.question_id = id;

      if (id) {
        await editQuestion(payload);
        handleSuccess("Question updated successfully!");
      } else {
        await addQuestion(payload);
        handleSuccess("Question added successfully!");
      }

      setTimeout(() => navigate("/questions"), 1200);

    } catch (err) {
      showErrorAlert(err?.response?.data?.messages?.error || "Error saving question");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = () => {
    setIsToggleOn(!isToggleOn)
  }

  const handleOptionChange = (index, value, type) => {
    if (type === "multiple_choice") {
      const updatedOptions = [...multipleChoiceOptions]
      updatedOptions[index].option_name = value
      setMultipleChoiceOptions(updatedOptions)
    } else {
      const updatedOptions = [...checkboxOptions]
      updatedOptions[index].option_name = value
      setCheckboxOptions(updatedOptions)
    }
  }

  const addhandleopn = () => {
    setCheckboxOptions([...checkboxOptions, { option_name: "", option_type: "neutral", medicines: [] }])
  }

  const handleMultipleChoiceChange = (index, value) => {
    const updatedOptions = [...multipleChoiceOptions]
    updatedOptions[index].option_name = value
    setMultipleChoiceOptions(updatedOptions)
  }

  const addMultipleChoiceOption = () => {
    setMultipleChoiceOptions([...multipleChoiceOptions, { option_name: "", option_type: "neutral", medicines: [], note_category_id: null }])
  }

  const deleteMultipleChoiceOption = (indexToDelete) => {
    const updatedOptions = multipleChoiceOptions.filter((_, index) => index !== indexToDelete)
    setMultipleChoiceOptions(updatedOptions)
  }

  const deleteChoiceOption = (index) => {
    const updatedOptions = checkboxOptions.filter((_, i) => i !== index)
    setCheckboxOptions(updatedOptions)
  }

  const handleFirstSwitchChange = () => {
    setIsFirstSwitchOn((prevState) => {
      const newState = !prevState
      return newState
    })
  }

  // FIXED: Radio button handler for multiple choice options
  const handleSentimentChange = (index, sentimentType) => {
    const updated = [...multipleChoiceOptions];
    updated[index].option_type = sentimentType;
    setMultipleChoiceOptions(updated);
  };

  const handleCheckboxSentimentChange = (index, sentimentType) => {
    const updated = [...checkboxOptions];
    updated[index].option_type = sentimentType;
    setCheckboxOptions(updated);
  };

  const handleValidationTypeChange = (index, selectedOption) => {
    const newFields = [...fields]
    newFields[index].validationType = selectedOption.value
    newFields[index].criteria = ""
    setFields(newFields)
  }

  const handleCriteriaChange = (index, selectedOption) => {
    const newFields = [...fields]
    newFields[index].criteria = selectedOption.value
    setFields(newFields)
  }

  const handleFieldChange = (index, e) => {
    const { name, value } = e.target;
    const newFields = [...fields];

    if (!newFields[index].error) newFields[index].error = "";

    const validationType = newFields[index].validationType;

    if (validationType === "number" && name === "field1") {
      if (/^[0-9]*\.?[0-9]*$/.test(value) || value === "") {
        newFields[index][name] = value;
        newFields[index].error = "";
      } else {
        newFields[index].error = "Please enter numbers only";
        setFields(newFields);
        return;
      }
    }
    else if (validationType === "text" && name === "field1") {
      if (/^[A-Za-z\s]*$/.test(value) || value === "") {
        newFields[index][name] = value;
        newFields[index].error = "";
      } else {
        newFields[index].error = "Please enter text only";
        setFields(newFields);
        return;
      }
    }
    else {
      newFields[index][name] = value;
      newFields[index].error = "";
    }

    setFields(newFields);
  };

  const handleAddField = (e) => {
    e.preventDefault()
    setFields([...fields, { validationType: "", criteria: "", field1: "", field2: "" }])
  }

  const handleDelete = (index) => {
    const newFields = fields.filter((_, i) => i !== index)
    setFields(newFields)
  }

  const getPlaceholderText = (validationType) => {
    switch (validationType) {
      case "number":
        return "Enter a number "
      case "text":
        return "Enter text here"
      case "length":
        return "Enter length "
      case "regex":
        return "Enter pattern "
      default:
        return "Enter value"
    }
  }

  const getDisplayName = (validationType) => {
    switch (validationType) {
      case "length":
        return "Enter length "
      case "regex":
        return "Enter pattern "
      default:
        return "Enter value"
    }
  }

  if (fetching) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Loader size="lg" />
      </div>
    )
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-body">
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "20px",
              }}
            >
              <Link to="/questions" className="btn btn-link">
                <Button variant="primary">Back</Button>
              </Link>
            </div>
            {error && <div className="text-danger mb-2">{error}</div>}
            {loading && (
              <div className="text-center">
                <Loader size="md" />
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col-xl-8">
                  <div className="form-group">
                    <label className="form-label">
                      Question:
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control "
                      value={question}
                      onChange={handleQuestionChange}
                      placeholder="Enter your question here"
                      required
                    />
                  </div>

                  {/* Short Form Field */}
                  <div className="form-group mt-3">
                    <label className="form-label">
                      Short Form:
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={shortForm}
                      onChange={(e) => setShortForm(e.target.value)}
                      placeholder="Enter short form e.g. BP, HR, HbA1c"
                    />
                  </div>
                </div>

                <div className="col-xl-4">
                  <div className="form-group">
                    <label className="form-label">Select Type:</label>
                    <Select
                      options={questionTypeOptions}
                      value={questionTypeOptions.find((option) => option.value === questionType)}
                      onChange={handleTypeChange}
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      isSearchable={false}
                    />
                  </div>
                </div>
              </div>

              {questionType === "short_answer" && (
                <>
                  {fields.map((field, index) => (
                    <div key={index} className="row mb-3">
                      <div className="col-xl-4">
                        <label className="form-label">Response Validation:</label>
                        <div className="form-group">
                          <Select
                            options={validationTypeOptions}
                            value={validationTypeOptions.find((option) => option.value === field.validationType)}
                            onChange={(selectedOption) => handleValidationTypeChange(index, selectedOption)}
                            styles={customStyles}
                            menuPortalTarget={document.body}
                            isSearchable={false}
                          />
                        </div>
                      </div>
                      {field.validationType && (
                        <div className="col-xl-4">
                          <label className="form-label">Validation Criteria:</label>
                          <div className="form-group">
                            <Select
                              options={getCriteriaOptions(field.validationType)}
                              value={getCriteriaOptions(field.validationType).find(
                                (option) => option.value === field.criteria,
                              )}
                              onChange={(selectedOption) => handleCriteriaChange(index, selectedOption)}
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={false}
                            />
                          </div>
                        </div>
                      )}
                      {field.validationType && (
                        <>
                          <div className="col-xl-2">
                            <label className="form-label">Enter Response:</label>
                            <input
                              type="text"
                              className="form-control"
                              name="field1"
                              value={field.field1}
                              onChange={(e) => handleFieldChange(index, e)}
                              placeholder={getPlaceholderText(field.validationType)}
                            />
                            {field.error && (
                              <small className="text-danger">{field.error}</small>
                            )}
                          </div>
                          <div className="col-xl-2">
                            <button
                              type="button"
                              className="btn btn-link text-danger"
                              onClick={() => handleDelete(index)}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                padding: "0",
                              }}
                            >
                              <i className="fa fa-times" style={{ fontSize: "18px", marginRight: "5px" }}></i>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  <div className="row mb-3">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-info" onClick={handleAddField}>
                        Add Validation
                      </button>
                    </div>
                  </div>
                </>
              )}

              {questionType === "paragraph" && (
                <>
                  {fields.map((field, index) => (
                    <div key={index} className="row mb-3">
                      <div className="col-xl-4">
                        <label className="form-label">Response Validation:</label>
                        <div className="form-group">
                          <Select
                            options={[
                              { value: "", label: "Select Validation Type" },
                              { value: "length", label: "Length" },
                              { value: "regex", label: "Regular Expression" },
                            ]}
                            value={[
                              { value: "", label: "Select Validation Type" },
                              { value: "length", label: "Length" },
                              { value: "regex", label: "Regular Expression" },
                            ].find((option) => option.value === field.validationType)}
                            onChange={(selectedOption) => handleValidationTypeChange(index, selectedOption)}
                            styles={customStyles}
                            menuPortalTarget={document.body}
                            isSearchable={false}
                          />
                        </div>
                      </div>
                      {field.validationType && (
                        <div className="col-xl-4">
                          <label className="form-label">Validation Criteria:</label>
                          <div className="form-group">
                            <Select
                              options={getCriteriaOptions(field.validationType)}
                              value={getCriteriaOptions(field.validationType).find(
                                (option) => option.value === field.criteria,
                              )}
                              onChange={(selectedOption) => handleCriteriaChange(index, selectedOption)}
                              styles={customStyles}
                              menuPortalTarget={document.body}
                              isSearchable={false}
                            />
                          </div>
                        </div>
                      )}
                      {field.validationType && (
                        <>
                          <div className="col-xl-2">
                            <label className="form-label">Enter Response:</label>
                            <input
                              type="text"
                              className="form-control"
                              name="field1"
                              value={field.field1}
                              onChange={(e) => handleFieldChange(index, e)}
                              placeholder={getDisplayName(field.validationType)}
                            />
                          </div>
                        </>
                      )}
                      <div className="col-xl-2">
                        <button
                          type="button"
                          className="btn btn-link text-danger"
                          onClick={() => handleDelete(index)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0",
                            height: "100%",
                          }}
                        >
                          <i className="fa fa-times" style={{ fontSize: "18px", marginRight: "5px" }}></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="row mb-3">
                    <div className="col-12 text-right">
                      <button type="button" className="btn btn-info" onClick={handleAddField}>
                        Add Validation
                      </button>
                    </div>
                  </div>
                </>
              )}

              {questionType === "File Input" && (
                <>
                  {fields.map((field, index) => (
                    <div key={index} className="row mb-3 align-items-end">
                      <div className="col-xl-4 col-lg-4 col-md-6 mb-2">
                        <label className="form-label">Allowed File Type:</label>
                        <Select
                          isMulti
                          options={[
                            { value: "pdf", label: "PDF" },
                            { value: "doc", label: "DOC" },
                            { value: "png", label: "PNG" },
                            { value: "jpeg", label: "JPEG" },
                          ]}
                          value={[
                            { value: "pdf", label: "PDF" },
                            { value: "doc", label: "DOC" },
                            { value: "png", label: "PNG" },
                            { value: "jpeg", label: "JPEG" },
                          ].filter((option) =>
                            (field.fileType || []).includes(option.value)
                          )}
                          onChange={(selectedOptions) =>
                            handleFieldChange(index, {
                              target: {
                                name: "fileType",
                                value: selectedOptions
                                  ? selectedOptions.map((opt) => opt.value)
                                  : [],
                              },
                            })
                          }
                          styles={customStyles}
                          menuPortalTarget={document.body}
                        />
                      </div>

                      <div className="col-xl-4 col-lg-4 col-md-6 mb-2">
                        <label className="form-label ">Max File Size (MB):</label>
                        <input
                          type="number"
                          className="form-control"
                          name="maxSize"
                          value={field.maxSize || ""}
                          onChange={(e) => handleFieldChange(index, e)}
                          placeholder="Enter max size e.g. 5"
                          min="1"
                          style={{ height: "45px" }}
                        />
                      </div>

                      <div className="col-xl-3 col-lg-3 col-md-6 mb-2">
                        <label className="form-label ">File Label:</label>
                        <input
                          type="text"
                          className="form-control"
                          name="label"
                          value={field.label || ""}
                          onChange={(e) => handleFieldChange(index, e)}
                          placeholder="e.g. Upload ID Proof"
                          style={{ height: "45px" }}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Multiple Selection (Checkbox) - This is for Single Selection questions */}
              {questionType === "checkbox" && (
                <div className="form-group col-xl-12">
                  <label className="form-label">Select Options:</label>
                  {multipleChoiceOptions.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                        flexWrap: "wrap",
                        gap: "10px"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", flexGrow: 1, minWidth: "200px" }}>
                        <input
                          className="form-control"
                          type="text"
                          value={option.option_name}
                          onChange={(e) => handleMultipleChoiceChange(index, e.target.value)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "positive"}
                            onChange={() => handleSentimentChange(index, "positive")}
                          />
                          Positive
                        </label>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "negative"}
                            onChange={() => handleSentimentChange(index, "negative")}
                          />
                          Negative
                        </label>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "neutral"}
                            onChange={() => handleSentimentChange(index, "neutral")}
                          />
                          Neutral
                        </label>
                        <div style={{ minWidth: "200px" }}>
                          <MedicineDropdown
                            isMulti={true}
                            value={(option.medicines || []).map(id => ({ value: id, label: '' }))}
                            onChange={(selected) => {
                              const updated = [...multipleChoiceOptions]
                              updated[index].medicines = selected ? selected.map((s) => s.value) : []
                              setMultipleChoiceOptions(updated)
                            }}
                          />
                        </div>
                        <div style={{ minWidth: "200px" }}>
                          <Select
                            options={noteCategories.map((cat) => ({
                              value: cat.id,
                              label: cat.name
                            }))}
                            value={noteCategories
                              .map((cat) => ({
                                value: cat.id,
                                label: cat.name
                              }))
                              .find((opt) => opt.value == option.note_category_id) || null
                            }
                            onChange={(selected) => {
                              const updated = [...multipleChoiceOptions]
                              updated[index].note_category_id = selected?.value || null
                              setMultipleChoiceOptions(updated)
                            }}
                            placeholder="Select Note Category"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-link text-danger"
                          onClick={() => deleteMultipleChoiceOption(index)}
                        >
                          <i className="fa fa-times" style={{ fontSize: "18px" }}></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-link mt-2" onClick={addMultipleChoiceOption}>
                    Add Option
                  </button>
                  <div className="form-check form-switch mt-3">
                    <div className="d-flex align-items-center">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id="allowOtherOptionSwitch"
                        checked={allowOtherOption}
                        onChange={() => setAllowOtherOption(!allowOtherOption)}
                      />
                      <label className="form-check-label mb-0" htmlFor="allowOtherOptionSwitch">
                        {allowOtherOption ? "Remove Other as an option" : "Add Other as an option"}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Hidden checkbox options - kept for compatibility but not displayed */}
              {questionType === "checkbox" && (
                <div className="form-group col-xl-12" style={{ display: "none" }}>
                  {/* Hidden content kept for API compatibility */}
                </div>
              )}

              {/* Multiple Selection (multiple_choice) */}
              {questionType === "multiple_choice" && (
                <div className="form-group col-xl-12">
                  <label className="form-label">Select Options:</label>
                  {multipleChoiceOptions.map((option, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                        flexWrap: "wrap",
                        gap: "10px"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", flexGrow: 1, minWidth: "200px" }}>
                        <input
                          className="form-control"
                          type="text"
                          value={option.option_name}
                          onChange={(e) => handleMultipleChoiceChange(index, e.target.value)}
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "positive"}
                            onChange={() => handleSentimentChange(index, "positive")}
                          />
                          Positive
                        </label>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "negative"}
                            onChange={() => handleSentimentChange(index, "negative")}
                          />
                          Negative
                        </label>
                        <label className="form-check-label" style={{ display: "flex", alignItems: "center" }}>
                          <input
                            type="radio"
                            name={`sentiment-${index}`}
                            className="form-check-input"
                            style={{ marginRight: "5px" }}
                            checked={option.option_type === "neutral"}
                            onChange={() => handleSentimentChange(index, "neutral")}
                          />
                          Neutral
                        </label>
                        <div style={{ minWidth: "200px" }}>
                          <MedicineDropdown
                            isMulti={true}
                            value={(option.medicines || []).map(id => ({ value: id, label: '' }))}
                            onChange={(selected) => {
                              const updated = [...multipleChoiceOptions]
                              updated[index].medicines = selected ? selected.map((s) => s.value) : []
                              setMultipleChoiceOptions(updated)
                            }}
                          />
                        </div>
                        <div style={{ minWidth: "200px" }}>
                          <Select
                            options={noteCategories.map((cat) => ({
                              value: cat.id,
                              label: cat.name
                            }))}
                            value={noteCategories
                              .map((cat) => ({
                                value: cat.id,
                                label: cat.name
                              }))
                              .find((opt) => opt.value == option.note_category_id) || null
                            }
                            onChange={(selected) => {
                              const updated = [...multipleChoiceOptions]
                              updated[index].note_category_id = selected?.value || null
                              setMultipleChoiceOptions(updated)
                            }}
                            placeholder="Select Note Category"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-link text-danger"
                          onClick={() => deleteMultipleChoiceOption(index)}
                        >
                          <i className="fa fa-times" style={{ fontSize: "18px" }}></i>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-link mt-2" onClick={addMultipleChoiceOption}>
                    Add Option
                  </button>
                  <div className="form-check form-switch mt-3">
                    <div className="d-flex align-items-center">
                      <input
                        className="form-check-input me-2"
                        type="checkbox"
                        id="allowOtherOptionSwitchMultiple"
                        checked={allowOtherOption}
                        onChange={() => setAllowOtherOption(!allowOtherOption)}
                      />
                      <label className="form-check-label mb-0" htmlFor="allowOtherOptionSwitchMultiple">
                        {allowOtherOption ? "Remove Other as an option" : "Add Other as an option"}
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <div className="form-check form-switch mt-3 d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="firstSwitch"
                    checked={isFirstSwitchOn}
                    onChange={handleFirstSwitchChange}
                  />
                  <label className="form-check-label" htmlFor="firstSwitch">
                    {isFirstSwitchOn ? " Important" : "Not Important"}
                  </label>
                </div>
                <div className="d-flex align-items-center mt-3">
                  <span className="text-gray-700 me-3">Required:</span>
                  <div className="form-check form-switch d-flex align-items-center">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flexSwitchCheckDefault"
                      checked={isToggleOn}
                      onChange={handleToggleChange}
                    />
                    <label className="form-check-label" htmlFor="flexSwitchCheckDefault">
                      {isToggleOn ? "On" : "Off"}
                    </label>
                  </div>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={loading || fetching}>
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">Submitting...</span>
                  </>
                ) : id ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AddQuestion
